import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Mail, Save, RotateCcw, FileText } from 'lucide-react';
import { getEmailTemplates, saveEmailTemplates } from '../services/emailService';
import { resetEmailTemplates } from '../services/apiService';

const PlantillasEmail = () => {
  const [templates, setTemplates] = useState({
    solicitudInicial: { asunto: '', cuerpo: '' },
    recordatorio: { asunto: '', cuerpo: '' },
    recordatorioFinal: { asunto: '', cuerpo: '' }
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('solicitudInicial');
  
  // Cargar plantillas desde la API al montar el componente
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const loadedTemplates = await getEmailTemplates();
        setTemplates(loadedTemplates);
      } catch (error) {
        console.error('Error cargando plantillas:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplates();
  }, []);

  const tabs = [
    { id: 'solicitudInicial', label: 'Solicitud Inicial', icon: Mail },
    { id: 'recordatorio', label: 'Recordatorio', icon: Mail },
    { id: 'recordatorioFinal', label: 'Recordatorio Final', icon: Mail }
  ];

  const handleChange = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: value
      }
    }));
  };

  const handleGuardar = async () => {
    try {
      await saveEmailTemplates(templates);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error guardando plantillas:', error);
      alert('Error al guardar las plantillas. Por favor, intenta de nuevo.');
    }
  };

  const handleRestaurar = async () => {
    if (window.confirm('¿Estás seguro de que quieres restaurar las plantillas por defecto? Se perderán todos los cambios no guardados.')) {
      try {
        setLoading(true);
        const response = await resetEmailTemplates();
        if (response.success) {
          setTemplates(response.templates);
          // Limpiar localStorage
          localStorage.removeItem('emailTemplates');
          alert('Plantillas restauradas correctamente');
        } else {
          throw new Error(response.error || 'Error al restaurar plantillas');
        }
      } catch (error) {
        console.error('Error restaurando plantillas:', error);
        alert('Error al restaurar las plantillas. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const variablesDisponibles = {
    '{startupNombre}': 'Nombre de la startup',
    '{fundadorNombre}': 'Nombre del fundador',
    '{reporteNombre}': 'Nombre del reporte (ej: Q1 2024)',
    '{arcangelNombre}': 'Nombre del arcángel'
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Plantillas de Email</h1>
          <p className="text-muted-foreground mt-2">
            Personaliza las plantillas de email que se envían a las startups para solicitar datos trimestrales
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando plantillas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Plantillas de Email</h1>
        <p className="text-muted-foreground mt-2">
          Personaliza las plantillas de email que se envían a las startups para solicitar datos trimestrales
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-2 border-b border-border/50">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }
                `}
              >
                <TabIcon className="h-4 w-4 inline mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRestaurar}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Restaurar por Defecto
          </Button>
          <Button onClick={handleGuardar}>
            <Save className="h-4 w-4 mr-2" />
            Guardar Cambios
          </Button>
        </div>
      </div>

      {saved && (
        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
          <p className="text-sm text-green-800 dark:text-green-200">
            ✓ Plantillas guardadas correctamente
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{tabs.find(t => t.id === activeTab)?.label}</CardTitle>
          <CardDescription>
            Personaliza el asunto y el cuerpo del email de {tabs.find(t => t.id === activeTab)?.label.toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
            <p className="text-xs font-medium mb-2">Variables disponibles:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(variablesDisponibles).map(([variable, descripcion]) => (
                <span
                  key={variable}
                  className="text-xs px-2 py-1 rounded bg-background border border-border/50"
                  title={descripcion}
                >
                  <code>{variable}</code>
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Estas variables se reemplazarán automáticamente con los datos reales al enviar el email
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Asunto del Email
            </label>
            <input
              type="text"
              value={templates[activeTab].asunto}
              onChange={(e) => handleChange('asunto', e.target.value)}
              className="w-full p-2 rounded-md border border-input bg-background"
              placeholder="Asunto del email"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Cuerpo del Email
            </label>
            <textarea
              value={templates[activeTab].cuerpo}
              onChange={(e) => handleChange('cuerpo', e.target.value)}
              className="w-full p-3 rounded-md border border-input bg-background font-mono text-sm min-h-[400px]"
              placeholder="Cuerpo del email"
            />
            <p className="text-xs text-muted-foreground">
              Puedes usar las variables disponibles arriba para personalizar el contenido
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Vista Previa</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="font-semibold">{templates[activeTab].asunto}</div>
              <div className="whitespace-pre-wrap text-muted-foreground">
                {templates[activeTab].cuerpo}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlantillasEmail;

